/**
 * "Leave Without Saving?": while `when` holds, leaving the page (another
 * route, Back, closing the tab) asks first. In-app navigation is held by
 * react-router's `useBlocker`, which needs the app's data router; under a
 * plain router (tests, embeds) only the tab-close guard applies.
 */
import { useContext, useEffect, type FC } from "react";
import {
  UNSAFE_DataRouterContext,
  useBlocker,
  type Location
} from "react-router-dom";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

type Props = {
  when: boolean;
  /** Which moves count as leaving; every move by default. */
  leaves?: (from: Location, to: Location) => boolean;
  /** Called on Discard Changes, before the navigation goes ahead. */
  onDiscard?: () => void;
};

const RouteGuard: FC<Required<Pick<Props, "when">> & Props> = ({
  when,
  leaves,
  onDiscard
}) => {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && (leaves ? leaves(currentLocation, nextLocation) : true)
  );
  const open = blocker.state === "blocked";
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && blocker.state === "blocked") blocker.reset();
      }}
    >
      <AlertDialogContent data-testid="unsaved-changes-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Leave Without Saving?</AlertDialogTitle>
          <AlertDialogDescription>
            Your changes to this automation haven’t been saved.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="unsaved-keep-editing">
            Keep Editing
          </AlertDialogCancel>
          <Button
            variant="danger"
            onClick={() => {
              onDiscard?.();
              if (blocker.state === "blocked") blocker.proceed();
            }}
            data-testid="unsaved-discard"
          >
            Discard Changes
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const UnsavedChangesGuard: FC<Props> = ({ when, leaves, onDiscard }) => {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  useEffect(() => {
    if (!when) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [when]);
  return dataRouter ? (
    <RouteGuard when={when} leaves={leaves} onDiscard={onDiscard} />
  ) : null;
};
